import { useState, useCallback } from 'react'
import type { FleetLogRow } from '@alpha-edge/shared'
import { LOG_EVENTS } from '@alpha-edge/shared'
import {
  PageHeader,
  DataTable,
  Modal,
  ColumnSettingsButton,
  colors,
  input,
  btnSecondary,
  btnDanger,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useApiList } from '../../hooks/use-api-list'
import type { Environment } from '@alpha-edge/shared'
import { relativeTime } from './fleet-helpers'
import { FleetLogDlqModal } from './FleetLogDlqModal'
import { useTableColumns } from '../../hooks/use-table-columns'

// ── styles ────────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '6px',
}

const chipStyle = (selected: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 12px',
  borderRadius: '9999px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  cursor: 'pointer',
  border: selected ? `2px solid ${colors.primary}` : `2px solid ${colors.border}`,
  background: selected ? '#eff6ff' : '#fff',
  color: selected ? colors.primary : colors.text,
  userSelect: 'none',
  transition: 'all 0.12s',
})

const chipRemoveStyle: React.CSSProperties = {
  marginLeft: '6px',
  fontSize: '0.75rem',
  cursor: 'pointer',
  color: colors.textMuted,
  lineHeight: 1,
}

const filterRowStyle: React.CSSProperties = {
  marginBottom: '16px',
}

// ── level badge ───────────────────────────────────────────────────────────────

const LEVEL_COLORS: Record<string, { bg: string; text: string }> = {
  trace: { bg: '#f3f4f6', text: '#6b7280' },
  debug: { bg: '#dbeafe', text: '#1d4ed8' },
  info: { bg: '#f0fdf4', text: '#15803d' },
  warn: { bg: '#fef9c3', text: '#a16207' },
  error: { bg: '#fee2e2', text: '#dc2626' },
  fatal: { bg: '#4c0519', text: '#fecdd3' },
}

function LevelBadge({ level }: { readonly level: string }) {
  const c = LEVEL_COLORS[level] ?? { bg: '#f3f4f6', text: '#6b7280' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 700,
        background: c.bg,
        color: c.text,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {level}
    </span>
  )
}

// ── helpers ───────────────────────────────────────────────────────────────────

function truncateMessage(msg: string): string {
  return msg.length > 80 ? msg.slice(0, 77) + '...' : msg
}

// ── detail modal ──────────────────────────────────────────────────────────────

interface DetailModalProps {
  readonly row: FleetLogRow | null
  readonly envName: string | null
  readonly onClose: () => void
}

function FleetLogDetailModal({ row, envName, onClose }: DetailModalProps) {
  if (!row) return null
  return (
    <Modal isOpen={row !== null} onClose={onClose} title="Log Entry Detail" width="680px">
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.875rem',
        }}
      >
        <tbody>
          {(
            [
              ['ID', row.id],
              ['Timestamp', new Date(row.ts).toLocaleString()],
              ['Ingested At', new Date(row.ingestedAt).toLocaleString()],
              ['Environment', envName ?? row.sourceEnvironmentId],
              ['Level', <LevelBadge key="level" level={row.level} />],
              ['Event', row.evt],
              ['Container', row.container],
              ['Message', row.message],
              ['Request ID', row.requestId ?? '—'],
              ['Batch ID', row.batchId ?? '—'],
              ['Org ID', row.organizationId ?? '—'],
              ['User ID', row.userId ?? '—'],
            ] as Array<[string, React.ReactNode]>
          ).map(([label, value]) => (
            <tr key={label} style={{ borderBottom: `1px solid ${colors.border}` }}>
              <td
                style={{
                  padding: '8px 12px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  width: '130px',
                  verticalAlign: 'top',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </td>
              <td
                style={{
                  padding: '8px 12px',
                  color: colors.text,
                  wordBreak: 'break-all',
                }}
              >
                {value}
              </td>
            </tr>
          ))}
          {row.bindings && (
            <tr>
              <td
                style={{
                  padding: '8px 12px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  verticalAlign: 'top',
                  whiteSpace: 'nowrap',
                }}
              >
                Bindings
              </td>
              <td style={{ padding: '8px 12px' }}>
                <pre
                  style={{
                    margin: 0,
                    fontFamily: 'monospace',
                    fontSize: '0.8125rem',
                    background: '#f8fafc',
                    padding: '8px',
                    borderRadius: '4px',
                    overflowX: 'auto',
                    color: colors.text,
                  }}
                >
                  {JSON.stringify(row.bindings, null, 2)}
                </pre>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Modal>
  )
}

// ── log levels ────────────────────────────────────────────────────────────────

const LOG_LEVELS = ['', 'trace', 'debug', 'info', 'warn', 'error', 'fatal'] as const
type LogLevelFilter = (typeof LOG_LEVELS)[number]

// ── component ─────────────────────────────────────────────────────────────────

export function FleetLogsPage() {
  const [selectedEnvIds, setSelectedEnvIds] = useState<readonly string[]>([])
  const [level, setLevel] = useState<LogLevelFilter>('')
  const [evt, setEvt] = useState('')
  const [requestId, setRequestId] = useState('')
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [dlqOpen, setDlqOpen] = useState(false)
  const [detailRow, setDetailRow] = useState<FleetLogRow | null>(null)
  const { tableId: _, ...tableColumnProps } = useTableColumns('fleet-logs')

  // Build filter object for useApiList — omit undefined so params aren't sent.
  const filters: Record<string, string | undefined> = {
    ...(selectedEnvIds.length > 0
      ? Object.fromEntries(selectedEnvIds.map((id, i) => [`environmentIds[${i}]`, id]))
      : {}),
    level: level || undefined,
    evt: evt || undefined,
    requestId: requestId || undefined,
    search: search || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to).toISOString() : undefined,
  }

  const {
    data: rows,
    total,
    loading,
    error,
  } = useApiList<FleetLogRow>({
    path: '/admin/fleet/logs',
    limit: 100,
    filters,
  })

  const { data: environments, loading: envsLoading } = useApiList<Environment>({
    path: '/fleet/environments',
  })

  const envById = new Map(environments.map((e) => [e.id, e.name]))

  const toggleEnv = useCallback((id: string) => {
    setSelectedEnvIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }, [])

  const hasFilters = selectedEnvIds.length > 0 || level || evt || requestId || search || from || to

  const clearFilters = useCallback(() => {
    setSelectedEnvIds([])
    setLevel('')
    setEvt('')
    setRequestId('')
    setSearch('')
    setFrom('')
    setTo('')
  }, [])

  const selectedEnvs = environments.filter((e) => selectedEnvIds.includes(e.id))
  const unselectedEnvs = environments.filter((e) => !selectedEnvIds.includes(e.id))

  const columns: readonly Column<FleetLogRow>[] = [
    {
      key: 'ts',
      header: 'Timestamp',
      render: (r) => relativeTime(r.ts),
      sortable: true,
    },
    {
      key: 'sourceEnvironmentId',
      header: 'Environment',
      render: (r) => envById.get(r.sourceEnvironmentId) ?? r.sourceEnvironmentId.slice(0, 8),
      sortable: true,
    },
    {
      key: 'level',
      header: 'Level',
      render: (r) => <LevelBadge level={r.level} />,
      sortable: true,
    },
    {
      key: 'evt',
      header: 'Event',
      render: (r) => <span style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{r.evt}</span>,
      sortable: true,
      filterable: true,
    },
    {
      key: 'container',
      header: 'Container',
      render: (r) => r.container,
      sortable: true,
    },
    {
      key: 'message',
      header: 'Message',
      render: (r) => (
        <span title={r.message} style={{ cursor: 'pointer', color: colors.text }} onClick={() => setDetailRow(r)}>
          {truncateMessage(r.message)}
        </span>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Fleet Logs"
        subtitle={`Structured log stream from all enrolled deployments${total > 0 ? ` — ${total.toLocaleString()} rows` : ''}`}
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />

      <div style={{ padding: '24px' }}>
        {/* Environment filter chips */}
        <div style={filterRowStyle}>
          <label style={labelStyle}>Filter by Environment (all environments if none selected)</label>
          {selectedEnvs.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              {selectedEnvs.map((env) => (
                <span key={env.id} style={chipStyle(true)}>
                  {env.name}
                  <span
                    style={chipRemoveStyle}
                    role="button"
                    onClick={() => toggleEnv(env.id)}
                    title={`Remove ${env.name}`}
                  >
                    x
                  </span>
                </span>
              ))}
            </div>
          )}
          {envsLoading ? (
            <span style={{ fontSize: '0.8125rem', color: colors.textMuted }}>Loading environments...</span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {unselectedEnvs.map((env) => (
                <span
                  key={env.id}
                  style={chipStyle(false)}
                  role="button"
                  onClick={() => toggleEnv(env.id)}
                  title={`Add ${env.name}`}
                >
                  {env.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Level + Event + Request ID filters */}
        <div style={{ ...filterRowStyle, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>Level</label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value as LogLevelFilter)}
              style={{ ...input, width: '130px' }}
            >
              {LOG_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l || '(all)'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Event</label>
            <select value={evt} onChange={(e) => setEvt(e.target.value)} style={{ ...input, width: '200px' }}>
              <option value="">(all)</option>
              {LOG_EVENTS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Request ID</label>
            <input
              type="text"
              placeholder="cross-service correlation"
              value={requestId}
              onChange={(e) => setRequestId(e.target.value)}
              style={{ ...input, width: '220px' }}
            />
          </div>

          <div>
            <label style={labelStyle}>Search Message</label>
            <input
              type="text"
              placeholder="keyword in message"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...input, width: '220px' }}
            />
          </div>
        </div>

        {/* Date range */}
        <div style={{ ...filterRowStyle, display: 'flex', gap: '16px' }}>
          <div>
            <label style={labelStyle}>From (event time)</label>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
          <div>
            <label style={labelStyle}>To (event time)</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
        </div>

        {/* Filter actions */}
        <div style={{ ...filterRowStyle, display: 'flex', gap: '12px', alignItems: 'center' }}>
          {hasFilters && (
            <button style={btnSecondary} onClick={clearFilters}>
              Clear filters
            </button>
          )}
          <button style={{ ...btnDanger, fontSize: '0.8125rem', padding: '6px 14px' }} onClick={() => setDlqOpen(true)}>
            Show quarantined rows
          </button>
        </div>

        {/* Error */}
        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading...</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            keyField="id"
            emptyMessage="No log rows found for the selected filters."
            onRowClick={(row) => setDetailRow(row)}
            {...tableColumnProps}
          />
        )}
      </div>

      {/* Row detail modal */}
      <FleetLogDetailModal
        row={detailRow}
        envName={detailRow ? (envById.get(detailRow.sourceEnvironmentId) ?? null) : null}
        onClose={() => setDetailRow(null)}
      />

      {/* DLQ modal */}
      <FleetLogDlqModal isOpen={dlqOpen} onClose={() => setDlqOpen(false)} />
    </div>
  )
}
